/**
 * /payment/return — ReservePay Return Page
 * Created: 2026-06-13
 *
 * ReservePay redirects the user here after hosted checkout completes.
 * Polls transaction status and shows success / failure UI.
 *
 * Transaction ID priority:
 *   1) ?txid= query param (set by backend in return_url)
 *   2) localStorage (stored by redirectToReservePayCheckout)
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DashboardLayout } from '@/components/dashboard/dashboard-layout'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react'
import { consumePendingTransaction, getPaymentStatus } from '@/lib/api/reservepay-payment'
import { ReservePayTransactionStatus } from '@/src/types/reservepay-payment'

const POLL_INTERVAL_MS = 3000
const MAX_POLLS = 40 // 2 minutes timeout

type UIState = 'loading' | 'success' | 'failed' | 'cancelled' | 'expired' | 'not_found'

interface TransactionInfo {
  id: string
  referenceNumber: string
  amount: number
  creditAmount?: number
  transactionType?: string
  packageName?: string
  completedAt?: string
}

export default function PaymentReturnPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [uiState, setUiState] = useState<UIState>('loading')
  const [transaction, setTransaction] = useState<TransactionInfo | null>(null)

  // Priority 1: ?txid= query param (set by backend in return_url sent to ReservePay)
  // Priority 2: localStorage (set by frontend redirectToReservePayCheckout)
  const urlTxid = searchParams.get('txid')
  const stored = consumePendingTransaction()
  const txid = urlTxid || stored?.txid || null
  const pendingType = stored?.type ?? 'credit'

  const pendingRef = useRef({ txid, type: pendingType })
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollCountRef = useRef(0)

  // Derive flow type — prefer transaction_type from API, fallback to sessionStorage
  const getIsCredit = (tx: TransactionInfo | null): boolean => {
    if (tx?.transactionType) {
      return tx.transactionType === 'credit_topup'
    }
    return pendingRef.current?.type === 'credit'
  }

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const checkStatus = async (txid: string) => {
    try {
      const res = await getPaymentStatus(txid)
      const tx = res.data?.transaction
      const status = tx?.status as ReservePayTransactionStatus | undefined

      if ((status === 'completed' || res.data?.isCompleted) && tx) {
        stopPolling()
        setTransaction({
          id: tx.id,
          referenceNumber: tx.reference_number,
          amount: tx.amount,
          creditAmount: tx.credit_amount,
          transactionType: tx.transaction_type,
          packageName: (tx as any).package_name,
          completedAt: tx.completed_at,
        })
        setUiState('success')
      } else if (status === 'cancelled' || status === 'rejected') {
        stopPolling()
        setTransaction({
          id: tx?.id ?? '',
          referenceNumber: tx?.reference_number ?? '',
          amount: tx?.amount ?? 0,
          creditAmount: tx?.credit_amount,
          transactionType: tx?.transaction_type,
          packageName: (tx as any)?.package_name,
        })
        setUiState('cancelled')
      } else if (status === 'failed' || res.data?.isFailed) {
        stopPolling()
        setUiState('failed')
      } else if (status === 'expired') {
        stopPolling()
        setUiState('expired')
      }
      // Otherwise keep polling (created, pending)
    } catch {
      // Silently retry — don't crash the polling loop on transient errors
    }
  }

  useEffect(() => {
    const pending = pendingRef.current

    if (!pending?.txid) {
      setUiState('not_found')
      return
    }

    const { txid } = pending

    // Run first check immediately, then poll
    checkStatus(txid)

    intervalRef.current = setInterval(() => {
      pollCountRef.current += 1
      if (pollCountRef.current >= MAX_POLLS) {
        stopPolling()
        setUiState('expired')
        return
      }
      checkStatus(txid)
    }, POLL_INTERVAL_MS)

    return () => stopPolling()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ──────────────────────────────────────────────
  // Render helpers
  // ──────────────────────────────────────────────

  const isCredit = getIsCredit(transaction)

  const renderContent = () => {
    switch (uiState) {
      case 'loading':
        return (
          <div className="flex flex-col items-center gap-6 py-12">
            <Loader2 className="w-16 h-16 animate-spin text-[#7FB069]" />
            <div className="text-center space-y-2">
              <h2 className="text-xl font-bold text-gray-800">กำลังตรวจสอบการชำระเงิน</h2>
              <p className="text-gray-500 text-sm">กรุณารอสักครู่ ระบบกำลังยืนยันการชำระเงินจาก ReservePay</p>
            </div>
            <div className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-2 h-2 bg-[#7FB069] rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          </div>
        )

      case 'success':
        return (
          <div className="flex flex-col items-center gap-6 py-12">
            <CheckCircle className="w-20 h-20 text-green-500" />
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-green-700">ชำระเงินสำเร็จ!</h2>
              {transaction && (
                <>
                  {isCredit && transaction.creditAmount ? (
                    <>
                      <p className="text-gray-600">
                        เติม <span className="font-semibold text-[#7FB069]">{transaction.creditAmount.toLocaleString()} เครดิต</span> เข้าบัญชีของคุณแล้ว
                      </p>
                      <p className="text-sm text-gray-400">
                        จำนวนเงิน {transaction.amount.toLocaleString()} บาท
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-600">
                        {transaction.packageName
                          ? `คุณได้ซื้อแพ็กเกจ "${transaction.packageName}" เรียบร้อยแล้ว`
                          : 'คุณได้ซื้อแพ็กเกจเรียบร้อยแล้ว'}
                      </p>
                      <p className="text-sm text-gray-400">
                        {transaction.packageName
                          ? 'แพ็กเกจจะถูกเปิดใช้งานในไม่ช้า'
                          : `แพ็กเกจจะถูกเปิดใช้งานในไม่ช้า จำนวน ${transaction.amount.toLocaleString()} บาท`}
                      </p>
                    </>
                  )}
                  <p className="text-sm text-gray-400">
                    หมายเลขอ้างอิง: {transaction.referenceNumber}
                  </p>
                </>
              )}
            </div>
            <div className="flex gap-3 mt-4">
              <Button
                onClick={() => navigate(isCredit ? '/user/add-credit' : '/settings/packages')}
                variant="outline"
              >
                {isCredit ? 'เติมเครดิตอีกครั้ง' : 'ดูแพ็กเกจ'}
              </Button>
              <Button
                className="bg-[#7FB069] hover:bg-[#6a9457] text-white"
                onClick={() => navigate('/dashboard')}
              >
                กลับหน้าหลัก
              </Button>
            </div>
          </div>
        )

      case 'failed':
        return (
          <div className="flex flex-col items-center gap-6 py-12">
            <XCircle className="w-20 h-20 text-red-500" />
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-red-700">
                {isCredit ? 'การเติมเครดิตล้มเหลว' : 'การซื้อแพ็กเกจล้มเหลว'}
              </h2>
              <p className="text-gray-500">
                {isCredit
                  ? 'การเติมเครดิตไม่สำเร็จ กรุณาลองใหม่อีกครั้ง หรือติดต่อเจ้าหน้าที่หากตัดเงินไปแล้ว'
                  : 'การซื้อแพ็กเกจไม่สำเร็จ กรุณาลองใหม่อีกครั้ง หรือติดต่อเจ้าหน้าที่หากตัดเงินไปแล้ว'}
              </p>
              {transaction?.referenceNumber && (
                <p className="text-sm text-gray-400">
                  หมายเลขอ้างอิง: {transaction.referenceNumber}
                </p>
              )}
            </div>
            <div className="flex gap-3 mt-4">
              <Button
                className="bg-[#7FB069] hover:bg-[#6a9457] text-white"
                onClick={() => navigate(isCredit ? '/user/add-credit' : '/settings/packages')}
              >
                {isCredit ? 'ลองเติมเครดิตใหม่' : 'ลองซื้อแพ็กเกจใหม่'}
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                กลับหน้าหลัก
              </Button>
            </div>
          </div>
        )

      case 'cancelled':
        return (
          <div className="flex flex-col items-center gap-6 py-12">
            <XCircle className="w-20 h-20 text-orange-500" />
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-orange-700">
                {isCredit ? 'การเติมเครดิตถูกยกเลิก' : 'การซื้อแพ็กเกจถูกยกเลิก'}
              </h2>
              <p className="text-gray-500">
                {isCredit
                  ? 'รายการเติมเครดิตถูกยกเลิก ไม่มีการตัดเงินจากบัญชีของคุณ'
                  : 'รายการซื้อแพ็กเกจถูกยกเลิก ไม่มีการตัดเงินจากบัญชีของคุณ'}
              </p>
              {transaction?.referenceNumber && (
                <p className="text-sm text-gray-400">
                  หมายเลขอ้างอิง: {transaction.referenceNumber}
                </p>
              )}
            </div>
            <div className="flex gap-3 mt-4">
              <Button
                className="bg-[#7FB069] hover:bg-[#6a9457] text-white"
                onClick={() => navigate(isCredit ? '/user/add-credit' : '/settings/packages')}
              >
                {isCredit ? 'สร้างรายการใหม่' : 'เลือกแพ็กเกจใหม่'}
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                กลับหน้าหลัก
              </Button>
            </div>
          </div>
        )

      case 'expired':
        return (
          <div className="flex flex-col items-center gap-6 py-12">
            <Clock className="w-20 h-20 text-yellow-500" />
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-yellow-700">รายการหมดอายุ</h2>
              <p className="text-gray-500">
                {isCredit
                  ? 'รายการเติมเครดิตหมดอายุแล้ว หรือใช้เวลายืนยันนานเกินไป'
                  : 'รายการซื้อแพ็กเกจหมดอายุแล้ว หรือใช้เวลายืนยันนานเกินไป'}
              </p>
              <p className="text-gray-400 text-sm">หากคุณชำระเงินแล้ว กรุณาติดต่อเจ้าหน้าที่</p>
            </div>
            <div className="flex gap-3 mt-4">
              <Button
                className="bg-[#7FB069] hover:bg-[#6a9457] text-white"
                onClick={() => navigate(isCredit ? '/user/add-credit' : '/settings/packages')}
              >
                {isCredit ? 'สร้างรายการเติมเงินใหม่' : 'เลือกแพ็กเกจใหม่'}
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                กลับหน้าหลัก
              </Button>
            </div>
          </div>
        )

      case 'not_found':
      default:
        return (
          <div className="flex flex-col items-center gap-6 py-12">
            <XCircle className="w-20 h-20 text-gray-400" />
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-gray-700">ไม่พบรายการชำระเงิน</h2>
              <p className="text-gray-500">ไม่พบข้อมูลรายการชำระเงิน อาจเกิดจากการเปิดหน้านี้โดยตรง</p>
            </div>
            <Button
              className="bg-[#7FB069] hover:bg-[#6a9457] text-white"
              onClick={() => navigate('/dashboard')}
            >
              กลับหน้าหลัก
            </Button>
          </div>
        )
    }
  }

  return (
    <DashboardLayout>
      <div className="max-w-xl mx-auto py-10 px-4">
        <Card className="p-8">{renderContent()}</Card>
      </div>
    </DashboardLayout>
  )
}
